import { distance } from '@turf/turf';
import type { StoragePlace } from 'shared/storageAreas/domain/storageArea';

export interface PlaceGeometry {
  place: StoragePlace;
  coordinates: number[][];
}

/**
 * The origin and edge basis vectors of a storage area rectangle, used to map
 * between a storage place's local coordinates (meters) and geographic
 * coordinates.
 */
export interface ShapeBasis {
  origin: number[];
  widthVector: number[];
  heightVector: number[];
  widthMeters: number;
  heightMeters: number;
}

/**
 * Derives the origin and edge basis vectors of a storage area rectangle from
 * its shape feature. Returns null when the geometry is not a usable rectangle.
 */
export function getShapeBasis(shapeFeature: GeoJSON.Feature): ShapeBasis | null {
  const geometry = shapeFeature.geometry;

  if (!geometry || geometry.type !== 'Polygon') return null;

  const ring = geometry.coordinates[0];

  if (!ring || ring.length < 4) return null;

  const origin = ring[0]!;
  const widthCorner = ring[1]!;
  const heightCorner = ring[ring.length - 2]!;

  const widthMeters = distance(origin, widthCorner, { units: 'meters' });
  const heightMeters = distance(origin, heightCorner, { units: 'meters' });

  if (widthMeters <= 0 || heightMeters <= 0) return null;

  const widthVector = [widthCorner[0]! - origin[0]!, widthCorner[1]! - origin[1]!];
  const heightVector = [heightCorner[0]! - origin[0]!, heightCorner[1]! - origin[1]!];

  return { origin, widthVector, heightVector, widthMeters, heightMeters };
}

/**
 * Converts a geographic displacement vector into a local displacement in meters
 * (x along the width edge, y along the height edge) of the storage area.
 */
export function localDeltaFromGeographic(basis: ShapeBasis, deltaGeographic: number[]): { x: number; y: number } {
  const wx = basis.widthVector[0]!;
  const wy = basis.widthVector[1]!;
  const hx = basis.heightVector[0]!;
  const hy = basis.heightVector[1]!;
  const dx = deltaGeographic[0]!;
  const dy = deltaGeographic[1]!;

  const det = wx * hy - hx * wy;
  if (det === 0) return { x: 0, y: 0 };

  const fractionX = (dx * hy - hx * dy) / det;
  const fractionY = (wx * dy - dx * wy) / det;

  return { x: fractionX * basis.widthMeters, y: fractionY * basis.heightMeters };
}

/**
 * Converts an absolute geographic point into local coordinates in meters
 * (x along the width edge, y along the height edge) of the storage area.
 */
export function localFromGeographic(basis: ShapeBasis, point: number[]): { x: number; y: number } {
  return localDeltaFromGeographic(basis, [point[0]! - basis.origin[0]!, point[1]! - basis.origin[1]!]);
}

/**
 * Converts a local coordinate (x along the width edge, y along the height edge,
 * both in meters) into a geographic point.
 */
export function geographicFromLocal(basis: ShapeBasis, x: number, y: number): number[] {
  const fractionX = x / basis.widthMeters;
  const fractionY = y / basis.heightMeters;
  return [
    basis.origin[0]! + fractionX * basis.widthVector[0]! + fractionY * basis.heightVector[0]!,
    basis.origin[1]! + fractionX * basis.widthVector[1]! + fractionY * basis.heightVector[1]!,
  ];
}

/**
 * Computes the geographic geometry of each storage place inside a storage area.
 *
 * A storage place has local coordinates (x, y) in millimeters relative to the
 * storage area rectangle origin, plus a length and width in millimeters. The
 * storage area geometry comes from the shape feature (a polygon rectangle). We
 * derive the rectangle origin and its two edge basis vectors and place each
 * storage place accordingly, following the storage area orientation.
 */
export function computePlaceGeometry(
  shapeFeature: GeoJSON.Feature,
  places: StoragePlace[],
): PlaceGeometry[] {
  const geometry = shapeFeature.geometry;

  if (!geometry || geometry.type !== 'Polygon') return [];

  const ring = geometry.coordinates[0];

  if (!ring || ring.length < 4) return [];

  const origin = ring[0]!;
  const widthCorner = ring[1]!;
  const heightCorner = ring[ring.length - 2]!;

  const widthMeters = distance(origin, widthCorner, { units: 'meters' });
  const heightMeters = distance(origin, heightCorner, { units: 'meters' });

  if (widthMeters <= 0 || heightMeters <= 0) return [];

  // Place dimensions are stored in millimeters; the area extents are in meters.
  const widthMillimeters = widthMeters * 1000;
  const heightMillimeters = heightMeters * 1000;

  const widthVector = [widthCorner[0]! - origin[0]!, widthCorner[1]! - origin[1]!];
  const heightVector = [heightCorner[0]! - origin[0]!, heightCorner[1]! - origin[1]!];

  function corner(fractionX: number, fractionY: number): number[] {
    return [
      origin[0]! + fractionX * widthVector[0]! + fractionY * heightVector[0]!,
      origin[1]! + fractionX * widthVector[1]! + fractionY * heightVector[1]!,
    ];
  }

  const result: PlaceGeometry[] = [];

  for (const place of places) {
    const fractionX0 = place.x / widthMillimeters;
    const fractionY0 = place.y / heightMillimeters;
    const fractionX1 = (place.x + place.width) / widthMillimeters;
    const fractionY1 = (place.y + place.length) / heightMillimeters;

    const c00 = corner(fractionX0, fractionY0);
    const c10 = corner(fractionX1, fractionY0);
    const c11 = corner(fractionX1, fractionY1);
    const c01 = corner(fractionX0, fractionY1);

    result.push({
      place,
      coordinates: [c00, c10, c11, c01, c00],
    });
  }

  return result;
}
